import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faBan } from '@fortawesome/free-solid-svg-icons';

/**
 * A listagem em formato de cartão, para o celular.
 *
 * A tabela compartilhada tem largura mínima de 700px e rola dentro do próprio
 * container. Numa tela de 390px isso deixa visíveis só o número e o nome: a
 * situação do protocolo e os botões de Editar e Anular ficam fora da tela, sem
 * nenhum sinal de que existem. Quem atende pelo celular não descobre a ação.
 *
 * No cartão tudo que importa cabe na vertical, e a ação é um alvo de 44px.
 */
const ProtocoloCards = ({
  protocolos,
  carregando,
  rotuloTipo,
  formatarData,
  podeEditar,
  podeAnular,
  dicaVazio = 'Revise os filtros aplicados.',
  onAnular,
}) => {
  if (carregando) {
    return (
      <div className="py-10 text-center text-sm text-gray-500" data-testid="cards-carregando">
        <span className="inline-block w-5 h-5 mr-2 align-middle border-2 border-gray-300 border-t-green-600 rounded-full animate-spin" />
        Carregando registros...
      </div>
    );
  }

  if (!protocolos.length) {
    return (
      <div className="py-10 text-center">
        <p className="text-sm font-medium text-gray-700">Nenhum protocolo encontrado.</p>
        <p className="mt-1 text-sm text-gray-500">{dicaVazio}</p>
      </div>
    );
  }

  return (
    <ul className="space-y-3">
      {protocolos.map((protocolo) => {
        const anulado = protocolo.status === 'ANULADO';

        return (
          <li
            key={protocolo.id}
            className="rounded border border-gray-200 bg-white p-3 shadow-sm"
          >
            <div className="flex items-start justify-between gap-2">
              <span
                className={`text-lg font-bold ${anulado ? 'line-through text-gray-400' : 'text-gray-800'}`}
              >
                {protocolo.numero}
              </span>
              {anulado ? (
                <span
                  data-testid="situacao-protocolo"
                  className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700"
                  title={protocolo.motivo_anulacao || 'Protocolo anulado'}
                >
                  Anulado
                </span>
              ) : (
                <span
                  data-testid="situacao-protocolo"
                  className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700"
                >
                  Ativo
                </span>
              )}
            </div>

            <p className="mt-1 text-sm font-medium text-gray-800 break-words">
              {protocolo.requerente_nome}
            </p>

            <dl className="mt-2 space-y-1 text-sm text-gray-600">
              <div className="flex gap-2">
                <dt className="shrink-0 text-gray-500">Tipo:</dt>
                <dd className="break-words">{rotuloTipo(protocolo.tipo_requerimento)}</dd>
              </div>
              {protocolo.cursoNome && (
                <div className="flex gap-2">
                  <dt className="shrink-0 text-gray-500">Curso:</dt>
                  <dd className="break-words">{protocolo.cursoNome}</dd>
                </div>
              )}
              <div className="flex gap-2">
                <dt className="shrink-0 text-gray-500">Extensão:</dt>
                <dd className="break-words">{protocolo.extensaoNome || '-'}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="shrink-0 text-gray-500">Entrada:</dt>
                <dd>
                  {formatarData(protocolo.data_entrada)}
                  {protocolo.origem === 'AUTOMATICO' && (
                    <span className="text-gray-500"> · pedido {protocolo.pedido}</span>
                  )}
                </dd>
              </div>
            </dl>

            {!anulado && (podeEditar || podeAnular) && (
              <div className="mt-3 flex gap-2 border-t border-gray-100 pt-3">
                {podeEditar && (
                  <Link
                    to={`/protocolos/${protocolo.id}/edit`}
                    className="flex-1 min-h-11 rounded border border-green-600 text-green-700 font-medium hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center justify-center gap-2"
                  >
                    <FontAwesomeIcon icon={faPen} />
                    <span>Editar</span>
                  </Link>
                )}
                {podeAnular && (
                  <button
                    type="button"
                    onClick={() => onAnular(protocolo)}
                    className="flex-1 min-h-11 rounded border border-red-600 text-red-700 font-medium hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400 flex items-center justify-center gap-2"
                  >
                    <FontAwesomeIcon icon={faBan} />
                    <span>Anular</span>
                  </button>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

export default ProtocoloCards;
